import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

import member1 from "../../assets/brad.jpeg";
import member2 from "../../assets/daniyal1.png";
import member3 from "../../assets/farheen.jpeg";
import member4 from "../../assets/sarfraz.jpeg";
import member5 from "../../assets/sujata.jpeg";
import member6 from "../../assets/sunny.jpeg";
import member7 from "../../assets/vinay.jpeg";
import member8 from "../../assets/sayeed.jpeg";

// TODO: har member ki asli LinkedIn profile URL yahan daal do
const BOARD_MEMBERS = [
  { name: "Sarfraz Khan", role: "Board Member", image: member4, linkedin: "https://www.linkedin.com/in/iamsarfrazkhan" },
  { name: "Mirza Ali Danyal", role: "Board Member", image: member2, linkedin: "" },
  { name: "Brad Howard", role: "Board Member", image: member1, linkedin: "" },
  { name: "Sayeed Afridi", role: "Board Member", image: member8, linkedin: "" },
  { name: "Farheen Nisha", role: "Board Member", image: member3, linkedin: "" },
  { name: "Sujata Arora", role: "Board Member", image: member5, linkedin: "" },
  { name: "Vinay Arora", role: "Board Member", image: member7, linkedin: "" },
  { name: "Sunny Ahlawat", role: "Board Member", image: member6, linkedin: "" },
];

const AUTOPLAY_MS = 3500;

const getInitials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

// Brand icon (lucide ke naye versions mein Linkedin icon nahi hai, isliye inline SVG)
function LinkedInIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

export function BoardMembersSection() {
  const trackRef = useRef(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [pages, setPages] = useState(BOARD_MEMBERS.length);
  const [paused, setPaused] = useState(false);

  // Ek card ka step aur last reachable index nikalta hai (screen size ke hisaab se)
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.children.length < 2) return { step: 0, maxIndex: 0 };
    const step = track.children[1].offsetLeft - track.children[0].offsetLeft;
    const maxIndex = Math.max(0, Math.round((track.scrollWidth - track.clientWidth) / step));
    return { step, maxIndex };
  }, []);

  const goTo = useCallback(
    (index) => {
      const track = trackRef.current;
      if (!track) return;
      const { step, maxIndex } = measure();
      const safe = Math.min(Math.max(index, 0), maxIndex);
      track.scrollTo({ left: safe * step, behavior: "smooth" });
    },
    [measure]
  );

  const next = () => {
    const { maxIndex } = measure();
    goTo(activeRef.current >= maxIndex ? 0 : activeRef.current + 1);
  };

  const prev = () => {
    const { maxIndex } = measure();
    goTo(activeRef.current <= 0 ? maxIndex : activeRef.current - 1);
  };

  // Scroll (swipe / arrow / autoplay) ke saath active dot update
  const handleScroll = () => {
    const track = trackRef.current;
    const { step, maxIndex } = measure();
    if (!track || !step) return;
    const idx = Math.min(Math.round(track.scrollLeft / step), maxIndex);
    activeRef.current = idx;
    setActive(idx);
  };

  // Screen resize pe dots ki count update
  useEffect(() => {
    const update = () => setPages(measure().maxIndex + 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [measure]);

  // Autoplay
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      const { maxIndex } = measure();
      goTo(activeRef.current >= maxIndex ? 0 : activeRef.current + 1);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, measure, goTo]);

  return (
    <section className="relative bg-slate-950 text-white overflow-hidden border-y border-slate-800">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/60 to-slate-950" />
      <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-[#F5A623]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block rounded-full bg-amber-500/15 border border-[#F5A623]/50 text-[#F5A623] text-xs font-bold uppercase tracking-widest px-3 py-1 mb-4">
            Our Board
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
            People Behind The <span className="text-[#F5A623]">Vision</span>
          </h2>
        </motion.div>

        {/* Slider */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setTimeout(() => setPaused(false), 2500)}
        >
          <div
            ref={trackRef}
            onScroll={handleScroll}
            data-testid="board-slider"
            className="flex gap-3 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {BOARD_MEMBERS.map((member, i) => (
              <div
                key={member.name}
                data-testid={`board-member-${i}`}
                className="group relative shrink-0 snap-start basis-[62%] sm:basis-[calc((100%-2.5rem)/3)] lg:basis-[calc((100%-3.75rem)/4)] overflow-hidden rounded-2xl border border-white/10 bg-slate-900 transition-all duration-500 hover:-translate-y-1 hover:border-[#F5A623]/60 hover:shadow-2xl hover:shadow-[#F5A623]/10"
              >
                <div className="aspect-[4/5] w-full overflow-hidden bg-slate-800">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#F5A623]/15 text-[#F5A623] text-5xl font-extrabold">
                      {getInitials(member.name)}
                    </div>
                  )}
                </div>

                {/* Gradient + name overlay */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <span className="mb-2 block h-1 w-8 rounded-full bg-[#F5A623] transition-all duration-500 group-hover:w-14" />
                  <h3 className="font-heading text-base sm:text-lg font-bold text-white leading-tight">
                    {member.name}
                  </h3>
                  <p className="mt-0.5 text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#F5A623]">
                    {member.role}
                  </p>

                  {/* LinkedIn button */}
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${member.name} on LinkedIn`}
                      data-testid={`board-linkedin-${i}`}
                      draggable={false}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0A66C2] px-3 py-1.5 text-xs font-semibold text-white shadow-md transition-all duration-300 hover:bg-[#0b78e3] hover:shadow-lg hover:shadow-[#0A66C2]/40 hover:scale-105"
                    >
                      <LinkedInIcon className="h-3.5 w-3.5" />
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="mt-8 flex items-center justify-center gap-5">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous member"
              data-testid="board-prev-btn"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-[#F5A623] hover:text-[#F5A623]"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: pages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === active ? "w-8 bg-[#F5A623]" : "w-2 bg-white/25 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              aria-label="Next member"
              data-testid="board-next-btn"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-[#F5A623] hover:text-[#F5A623]"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}